import { create } from 'zustand';
import {
  TagDefinition,
  TagValue,
  TagsClient,
  ProductTag,
  ProductTagWithRelations,
} from 'src/Clients/TagsClient';

interface TagsStoreState {
  tagDefinitions: TagDefinition[];
  tagValues: TagValue[];
  loading: boolean;
  productTags: ProductTag[];
  allProductTags: ProductTagWithRelations[];
  getTagDefinitionsByOrganizationId: (organizationId: string) => Promise<void>;
  createTagDefinition: (
    tagDefinition: Omit<TagDefinition, 'id' | 'created_at' | 'updated_at'>
  ) => Promise<TagDefinition>;
  updateTagDefinition: (
    tagDefinition: Omit<TagDefinition, 'created_at' | 'updated_at'>
  ) => Promise<void>;
  deleteTagDefinition: (tagDefinitionId: string) => Promise<void>;
  getTagValuesByOrganizationId: (organizationId: string) => Promise<void>;
  createTagValue: (
    tagValue: Omit<TagValue, 'id' | 'created_at' | 'updated_at'>
  ) => Promise<void>;
  updateTagValue: (
    tagValue: Omit<TagValue, 'created_at' | 'updated_at'>
  ) => Promise<void>;
  deleteTagValue: (tagValueId: string) => Promise<void>;
  addProductTag: (productId: string, tagValueId: string) => Promise<void>;
  removeProductTag: (productId: string, tagValueId: string) => Promise<void>;
  getProductTags: (productId: string) => Promise<ProductTag[]>;
  getProductTagsByOrganizationId: (organizationId: string) => Promise<void>;
}

export const useTagsStore = create<TagsStoreState>((set) => ({
  tagDefinitions: [],
  tagValues: [],
  productTags: [],
  allProductTags: [],
  loading: false,
  getTagDefinitionsByOrganizationId: async (organizationId: string) => {
    try {
      set({ loading: true });
      const tagDefinitions = await TagsClient.getTagDefinitionsByOrganizationId(
        organizationId
      );
      set({ tagDefinitions });
    } finally {
      set({ loading: false });
    }
  },
  getTagValuesByOrganizationId: async (organizationId: string) => {
    try {
      set({ loading: true });
      const tagValues = await TagsClient.getTagValuesByOrganizationId(
        organizationId
      );
      set({ tagValues });
    } finally {
      set({ loading: false });
    }
  },
  getProductTagsByOrganizationId: async (organizationId: string) => {
    try {
      set({ loading: true });
      const productTags = await TagsClient.getAllProductTags(organizationId);
      set({ allProductTags: productTags });
    } finally {
      set({ loading: false });
    }
  },
  async createTagDefinition(
    tagDefinition: Omit<TagDefinition, 'id' | 'created_at' | 'updated_at'>
  ) {
    set({ loading: true });
    try {
      const createdTagDefinition = await TagsClient.createTagDefinition(
        tagDefinition
      );
      const tagDefinitions = await TagsClient.getTagDefinitionsByOrganizationId(
        tagDefinition.organization_id
      );
      set({ tagDefinitions });
      return createdTagDefinition;
    } finally {
      set({ loading: false });
    }
  },

  async updateTagDefinition(
    tagDefinition: Omit<TagDefinition, 'created_at' | 'updated_at'>
  ) {
    set({ loading: true });
    try {
      await TagsClient.updateTagDefinition(tagDefinition);
      const tagDefinitions = await TagsClient.getTagDefinitionsByOrganizationId(
        tagDefinition.organization_id
      );
      set({ tagDefinitions });
    } finally {
      set({ loading: false });
    }
  },

  async deleteTagDefinition(tagDefinitionId: string) {
    set({ loading: true });
    try {
      await TagsClient.deleteTagDefinition(tagDefinitionId);
    } finally {
      set({ loading: false });
    }
  },

  async createTagValue(
    tagValue: Omit<TagValue, 'id' | 'created_at' | 'updated_at'>
  ) {
    set({ loading: true });
    try {
      await TagsClient.createTagValue(tagValue);
      const tagValues = await TagsClient.getTagValuesByOrganizationId(
        tagValue.organization_id
      );
      set({ tagValues });
    } finally {
      set({ loading: false });
    }
  },

  async updateTagValue(tagValue: Omit<TagValue, 'created_at' | 'updated_at'>) {
    set({ loading: true });
    try {
      await TagsClient.updateTagValue(tagValue);
    } finally {
      set({ loading: false });
    }
  },

  async deleteTagValue(tagValueId: string) {
    set({ loading: true });
    try {
      await TagsClient.deleteTagValue(tagValueId);
    } finally {
      set({ loading: false });
    }
  },

  async addProductTag(productId: string, tagValueId: string) {
    set({ loading: true });
    try {
      await TagsClient.addProductTag(productId, tagValueId);
    } finally {
      set({ loading: false });
    }
  },

  async removeProductTag(productId: string, tagValueId: string) {
    set({ loading: true });
    try {
      await TagsClient.removeProductTag(productId, tagValueId);
    } finally {
      set({ loading: false });
    }
  },

  async getProductTags(productId: string) {
    set({ loading: true });
    try {
      const productTags = await TagsClient.getProductTags(productId);
      set({ productTags });
      return productTags;
    } finally {
      set({ loading: false });
    }
  },
}));
